import cityTimezones from "city-timezones";

// Mapping of IANA timezone names to abbreviations
export const timezoneAbbreviations: { [key: string]: string } = {
  // North America - Eastern
  "America/New_York": "ET",
  "America/Detroit": "ET",
  "America/Toronto": "ET",
  "America/Montreal": "ET",
  "America/Thunder_Bay": "ET",

  // North America - Central
  "America/Chicago": "CT",
  "America/Winnipeg": "CT",
  "America/Mexico_City": "CT",
  "America/Regina": "CT",
  "America/Guatemala": "CT",

  // North America - Mountain
  "America/Denver": "MT",
  "America/Edmonton": "MT",
  "America/Boise": "MT",
  "America/Phoenix": "MT",

  // North America - Pacific
  "America/Los_Angeles": "PT",
  "America/Vancouver": "PT",
  "America/Tijuana": "PT",

  // North America - Alaska & Hawaii
  "America/Anchorage": "AKT",
  "Pacific/Honolulu": "HST",

  // Europe - Western
  "Europe/London": "GMT",
  "Europe/Dublin": "GMT",
  "Europe/Lisbon": "WET",

  // Europe - Central
  "Europe/Paris": "CET",
  "Europe/Berlin": "CET",
  "Europe/Madrid": "CET",
  "Europe/Rome": "CET",
  "Europe/Amsterdam": "CET",
  "Europe/Brussels": "CET",
  "Europe/Vienna": "CET",
  "Europe/Copenhagen": "CET",
  "Europe/Stockholm": "CET",
  "Europe/Oslo": "CET",

  // Europe - Eastern
  "Europe/Helsinki": "EET",
  "Europe/Athens": "EET",
  "Europe/Bucharest": "EET",
  "Europe/Istanbul": "TRT",

  // Asia - Western & Central
  "Asia/Dubai": "GST",
  "Asia/Muscat": "GST",
  "Asia/Kolkata": "IST",
  "Asia/Bangkok": "ICT",
  "Asia/Ho_Chi_Minh": "ICT",

  // Asia - Eastern
  "Asia/Shanghai": "CST",
  "Asia/Hong_Kong": "HKT",
  "Asia/Singapore": "SGT",
  "Asia/Tokyo": "JST",
  "Asia/Seoul": "KST",

  // Australia
  "Australia/Sydney": "AEST",
  "Australia/Melbourne": "AEST",
  "Australia/Brisbane": "AEST",
  "Australia/Adelaide": "ACST",
  "Australia/Darwin": "ACST",
  "Australia/Perth": "AWST",

  // New Zealand
  "Pacific/Auckland": "NZST",
  "Pacific/Fiji": "FJT",

  // South America
  "America/Sao_Paulo": "BRT",
  "America/Buenos_Aires": "ART",
  "America/Santiago": "CLT",

  // Russia
  "Europe/Moscow": "MSK",
  "Asia/Vladivostok": "VLAT",

  // Middle East
  "Asia/Jerusalem": "IST",
  "Asia/Tehran": "IRST",
  "Asia/Riyadh": "AST",

  // North America - Eastern (Additional cities)
  "America/Doral": "ET",
  "America/Lake_Buena_Vista": "ET",
  "America/Plymouth_Meeting": "ET",

  // North America - Central (Additional)
  "America/Costa_Rica": "CT",

  // North America - Mountain (Additional)
  "America/Sun_Valley": "MT",

  // Australia (Additional)
  "Australia/Hobart": "AEST",
};

export interface CityInput {
  city: string;
  country?: string;
  state?: string; // For US cities
}

// Module-level cache for resolved IANA zones. Keyed by the same shape we
// pass to findBestCityMatch so different state/country contexts don't collide.
// cityTimezones.lookupViaCity does a linear scan over ~7,000 rows; this keeps
// repeat lookups for the same headquarters city O(1).
const ianaCache = new Map<string, string | null>();

// UTC-offset cache, invalidated once per UTC day so DST flips eventually take.
const offsetCache = new Map<string, number>();
let offsetCacheDay = -1;

/**
 * Normalize a city input for the city-timezones lookup.
 *
 * For a structured CityInput (caller already parsed Google Places data, etc.),
 * we trust the city/state/country fields and only trim whitespace.
 *
 * For a bare string (free-text user entry like `headquarters_city`), we also
 * strip a trailing ", State" or ", State, Country" suffix so the lookup gets
 * the bare city name and findBestCityMatch can use the state/country to
 * disambiguate (e.g. "Atlanta, GA, USA" → city "Atlanta", state "GA",
 * country "USA"). The library is case-insensitive internally, so we don't
 * need to title-case.
 *
 * @param input - Raw user-supplied city string or pre-parsed CityInput.
 * @returns Normalized CityInput, or null if the input has no usable city.
 */
export function normalizeCityInput(input: CityInput | string): CityInput | null {
  if (typeof input !== "string") {
    const city = input.city.trim();
    if (!city) return null;
    return { city, state: input.state, country: input.country };
  }

  const raw = input.trim();
  if (!raw) return null;

  let city = raw;
  let state: string | undefined;
  let country: string | undefined;

  // Split off "<city>, <tail>" — tail may itself be "<state>, <country>".
  const firstComma = city.indexOf(",");
  if (firstComma !== -1) {
    const head = city.slice(0, firstComma).trim();
    const tail = city.slice(firstComma + 1).trim();
    if (head && tail) {
      city = head;
      const secondComma = tail.indexOf(",");
      if (secondComma !== -1) {
        const middle = tail.slice(0, secondComma).trim();
        const last = tail.slice(secondComma + 1).trim();
        if (middle.length === 2) state = middle.toUpperCase();
        if (last) country = last;
      } else if (tail.length === 2) {
        // A 2-letter tail could be a US state ("Austin, TX") or a country
        // code ("London, GB"). Set both — findBestCityMatch tries country
        // first and falls back to US state matching.
        const code = tail.toUpperCase();
        state = code;
        country = code;
      } else {
        country = tail;
      }
    }
  }

  return { city, state, country };
}

/**
 * Resolve a city to an IANA zone with module-level caching.
 *
 * @param input - Already-normalized CityInput.
 * @returns IANA timezone string, or null if no match.
 */
function lookupIanaCached(input: CityInput): string | null {
  const key = `${input.city}|${input.state ?? ""}|${input.country ?? ""}`;
  const cached = ianaCache.get(key);
  if (cached !== undefined) return cached;
  const matched = findBestCityMatch(cityTimezones.lookupViaCity(input.city), input);
  const result = matched?.timezone ?? null;
  ianaCache.set(key, result);
  return result;
}

/**
 * Resolve the live short timezone name (e.g. "EST", "PDT", "HKT") for an IANA zone at the
 * given instant. Returns null when:
 *  - the runtime returns an auto-generated `GMT±N` / `UTC±N` placeholder (noisy),
 *  - the IANA name is unknown to `Intl.DateTimeFormat`.
 *
 * Falls back to the curated {@link timezoneAbbreviations} entry when Intl can't produce a
 * meaningful short label, so callers still get a usable hint for zones the runtime treats
 * as offset-only.
 *
 * @param iana - IANA timezone identifier (e.g. "America/New_York"). When null/undefined, returns null.
 * @param at - Reference instant. Defaults to now. Pass an event date to get the correct DST variant.
 * @returns Short name like "EST" or null.
 */
export function getShortTimezoneName(iana: string | null | undefined, at: Date = new Date()): string | null {
  if (!iana) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: iana, timeZoneName: "short" }).formatToParts(at);
    const namePart = parts.find((p) => p.type === "timeZoneName")?.value;
    if (namePart && !/^GMT([+-]\d|$)/i.test(namePart) && !/^UTC([+-]\d|$)/i.test(namePart)) {
      return namePart;
    }
  } catch {
    // unknown zone — fall through
  }
  return timezoneAbbreviations[iana] ?? null;
}

/**
 * Resolve a `" EST"`-style suffix (with a leading space) to append to a time/range display.
 * Returns "" when no zone is set or the runtime can't produce a meaningful label, so callers
 * can unconditionally concatenate the result.
 *
 * Anchors the abbreviation lookup to `dateStr` (`YYYY-MM-DD`) so DST-sensitive zones return
 * the variant that applies on that day rather than today's variant. Falls back to noon-now
 * when no date is supplied.
 *
 * @param iana - IANA timezone identifier or null/undefined.
 * @param dateStr - `YYYY-MM-DD` anchor. Optional — defaults to "now".
 * @returns `" EST"` / `" EDT"` / `""`.
 */
export function getTimezoneSuffix(iana: string | null | undefined, dateStr?: string | null): string {
  if (!iana) return "";
  const at = dateStr ? new Date(`${dateStr}T12:00:00`) : new Date();
  const short = getShortTimezoneName(iana, at);
  return short ? ` ${short}` : "";
}

/**
 * IANA-zone allowlist — letter-led segments separated by `/`, with `_`, `+`, and `-` allowed
 * inside a segment. Rejects CR/LF, `;`, `:`, `,`, `"` and any other punctuation that would
 * break ICS framing or PostgREST filters when this value is interpolated downstream.
 */
const IANA_PATTERN = /^(UTC|[A-Za-z][A-Za-z0-9_+\-]*(\/[A-Za-z][A-Za-z0-9_+\-]*)+)$/;

/**
 * True iff `value` is a syntactically well-formed IANA zone the runtime tzdb recognizes.
 * Defense-in-depth for any path that interpolates a zone into a structured payload (DB write,
 * `.ics` `TZID=`, etc.) — call this at the trust boundary, then again at the consumer if it's
 * a separate process.
 *
 * {@link IANA_PATTERN} requires an `Area/Location` shape (or the literal `"UTC"`) because
 * `Intl.DateTimeFormat` alone isn't a strict IANA-zone check: ICU resolves legacy fixed-offset
 * abbreviations like `"PST"`/`"EST"` as backward-compat aliases (e.g. `"PST"` → `America/Los_Angeles`)
 * instead of throwing, so they'd otherwise slip through as "valid" despite never observing DST.
 * Every real zone this app produces (city-timezones lookups, the `TimezonePicker` dropdown,
 * hand-typed `Area/Location` ids) already matches this shape, so nothing legitimate is rejected.
 *
 * @param value - candidate string.
 * @returns true when `value` matches {@link IANA_PATTERN} and `Intl.DateTimeFormat` accepts it.
 */
export function isValidIanaTimeZone(value: unknown): value is string {
  if (typeof value !== "string" || !IANA_PATTERN.test(value)) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the current UTC offset in minutes for an IANA zone.
 *
 * Used for sorting users by "what time is it for them" rather than by IANA
 * string (alphabetical sort puts America/Chicago before Asia/Tokyo before
 * Europe/London — meaningless to humans).
 *
 * @param iana - IANA timezone identifier.
 * @param at - Reference instant. Defaults to now.
 * @returns Minutes east of UTC (e.g. -480 for Los Angeles in PST, +540 for Tokyo).
 */
export function getUtcOffsetMinutes(iana: string, at: Date = new Date()): number {
  const day = Math.floor(at.getTime() / 86_400_000);
  if (day !== offsetCacheDay) {
    offsetCache.clear();
    offsetCacheDay = day;
  }
  const cached = offsetCache.get(iana);
  if (cached !== undefined) return cached;

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: iana,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(at);
  const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUTC = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  // `|| 0` collapses -0 to +0 for predictable equality.
  const offset = Math.round((asUTC - at.getTime()) / 60_000) || 0;
  offsetCache.set(iana, offset);
  return offset;
}

/** Session-lifetime cache for {@link timezoneOptions}'s built list — see its doc comment. */
let cachedTimezoneOptions: { value: string; label: string }[] | null = null;

/**
 * Test-only: clears the {@link timezoneOptions} cache so a test can force a rebuild after changing
 * how `Intl.supportedValuesOf` is mocked. Not meant for production call sites.
 */
export function __resetTimezoneOptionsCache(): void {
  cachedTimezoneOptions = null;
}

/**
 * IANA zones for a time-zone picker: the full runtime list when `Intl.supportedValuesOf` exists,
 * else a small fallback. Entries are sorted by current UTC offset, then name, and labeled
 * `(UTC±HH:MM) Area/Location` with underscores replaced by spaces.
 *
 * Built once per session and cached: both scheduling modals call this on every open, and re-walking
 * ~450-600 zones (each an `Intl.DateTimeFormat` construction via `getUtcOffsetMinutes`) plus
 * re-sorting and re-labeling on every open was a visible cost with no benefit — the underlying
 * offsets barely change (the day-scoped `offsetCache` above already accounts for DST), so a slightly
 * stale label across a DST boundary within one session is an acceptable tradeoff for a picker.
 *
 * @returns Dropdown options with the IANA name as `value`.
 */
export function timezoneOptions(): { value: string; label: string }[] {
  if (cachedTimezoneOptions) return cachedTimezoneOptions;

  const withOffsets = (supported: readonly string[]) =>
    [...supported]
      .map((tz) => ({ tz, off: getUtcOffsetMinutes(tz) }))
      .sort((a, b) => a.off - b.off || a.tz.localeCompare(b.tz))
      .map(({ tz, off }) => {
        const sign = off >= 0 ? "+" : "-";
        const h = String(Math.floor(Math.abs(off) / 60)).padStart(2, "0");
        const m = String(Math.abs(off) % 60).padStart(2, "0");
        return { value: tz, label: `(UTC${sign}${h}:${m}) ${tz.replace(/_/g, " ")}` };
      });
  const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.(
    "timeZone"
  );
  cachedTimezoneOptions = supported?.length
    ? withOffsets(supported)
    : ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Europe/London"].map(
        (tz) => ({ value: tz, label: tz.replace(/_/g, " ") })
      );
  return cachedTimezoneOptions;
}

/**
 * The viewer's own IANA zone, for defaulting a time-zone picker.
 *
 * @returns The runtime's resolved zone, or `"UTC"` when it reports none or throws.
 */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Finds the best matching city from the city-timezones lookup results
 * @param cityLookup - Array of city-timezone lookup results
 * @param input - The city input with optional country and state
 * @returns The best matching city result, or null if no results
 */
function findBestCityMatch(
  cityLookup: ReturnType<typeof cityTimezones.lookupViaCity>,
  input: CityInput
): (typeof cityLookup)[0] | null {
  if (!cityLookup || cityLookup.length === 0) return null;

  let matchedCity = cityLookup[0];

  if (cityLookup.length > 1) {
    if (input.country) {
      const countryMatch = cityLookup.find(
        (city) =>
          city.country.toLowerCase() === input.country?.toLowerCase() ||
          city.iso2.toLowerCase() === input.country?.toLowerCase()
      );
      if (countryMatch) matchedCity = countryMatch;
    } else {
      const usMatch = cityLookup.find((city) => city.iso2 === "US");
      if (usMatch) matchedCity = usMatch;
    }

    if (input.state && matchedCity.iso2 === "US") {
      const stateMatch = cityLookup.find(
        (city) => city.iso2 === "US" && city.state_ansi?.toLowerCase() === input.state?.toLowerCase()
      );
      if (stateMatch) matchedCity = stateMatch;
    }
  }

  return matchedCity;
}

/**
 * Get the timezone abbreviation for a city (e.g., "ET", "PT")
 * @param cityInput - City name or object with city/country/state
 * @returns Timezone abbreviation string, or "-" if lookup fails
 */
export function getCityTimezone(cityInput: CityInput | string | null | undefined): string {
  if (!cityInput) return "-";

  try {
    const input = normalizeCityInput(cityInput);
    if (!input) return "-";

    const timezone = lookupIanaCached(input);
    if (!timezone) return typeof cityInput === "string" ? cityInput : cityInput.city;

    return timezoneAbbreviations[timezone] || timezone;
  } catch (error) {
    console.error("Error looking up timezone for city:", cityInput, error);
    return "-";
  }
}

/**
 * Get the IANA timezone identifier for a city (e.g., "America/New_York")
 * @param cityInput - City name or object with city/country/state
 * @returns IANA timezone string, or null if lookup fails
 */
export function getCityIANATimezone(cityInput: CityInput | string | null | undefined): string | null {
  if (!cityInput) return null;

  try {
    const input = normalizeCityInput(cityInput);
    if (!input) return null;
    return lookupIanaCached(input);
  } catch (error) {
    console.error("Error looking up IANA timezone for city:", cityInput, error);
    return null;
  }
}

/**
 * Get the current time formatted for a city's timezone
 * @param cityInput - City name or object with city/country/state
 * @returns Formatted time string, or "-" if lookup fails
 */
export function getCurrentTimeForCity(cityInput: CityInput | string | null | undefined): string {
  if (!cityInput) return "-";

  try {
    const input = normalizeCityInput(cityInput);
    if (!input) return "-";

    const timezone = lookupIanaCached(input);
    if (!timezone) return typeof cityInput === "string" ? cityInput : cityInput.city;

    return new Date().toLocaleTimeString("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZoneName: "short",
    });
  } catch (error) {
    console.error("Error getting current time for city:", cityInput, error);
    return "-";
  }
}
